import { useState } from 'react';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Popover from '@cloudscape-design/components/popover';
import { formatUnicodeCode, type DetectedCharacter } from '@utils/invisibleCharacterAnalyzer';

export interface InvisibleCharactersTextDisplayProps {
  text: string;
  detectedChars: DetectedCharacter[];
  showLineNumbers?: boolean;
  showSpaces?: boolean;
  showUnicode?: boolean;
  highlightedPosition?: number | null;
  allowCopyCharacter?: boolean;
  showActionButtons?: boolean;
  onRemoveInvisible?: () => void;
  onCopyCleaned?: () => void;
  onCharacterClick?: (charCode: number) => void;
}

export const InvisibleCharactersTextDisplay = ({
  text,
  detectedChars,
  showLineNumbers = true,
  showSpaces = false,
  showUnicode = false,
  highlightedPosition = null,
  allowCopyCharacter = true,
  showActionButtons = false,
  onRemoveInvisible,
  onCopyCleaned,
  onCharacterClick,
}: InvisibleCharactersTextDisplayProps) => {
  const [selectionTooltip, setSelectionTooltip] = useState<{
    text: string;
    position: number;
    x: number;
    y: number;
  } | null>(null);

  // Filter detected characters based on showSpaces
  const filteredChars = detectedChars.filter(char => 
    showSpaces || char.character.code !== 0x0020
  );

  const handleTextSelection = (event: React.MouseEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      const selectedText = selection.toString();
      const range = selection.getRangeAt(0);
      
      // Find the position of the selection start in the input text
      const preSelectionRange = range.cloneRange();
      preSelectionRange.selectNodeContents(event.currentTarget);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);
      
      // Get the text content before selection to calculate position
      const textBeforeSelection = preSelectionRange.toString();
      const position = textBeforeSelection.length;
      
      // Get mouse position for tooltip placement
      const rect = range.getBoundingClientRect();
      
      setSelectionTooltip({
        text: selectedText,
        position: position,
        x: rect.left + rect.width / 2,
        y: rect.top - 10
      });
    } else {
      setSelectionTooltip(null);
    }
  };

  const handleCharacterClick = (charCode: number) => {
    if (allowCopyCharacter && onCharacterClick) {
      onCharacterClick(charCode);
    }
  };

  const getCharacterColors = (category: string) => {
    switch (category) {
      case 'control':
        return { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' };
      case 'zero-width':
        return { bg: '#ffebee', border: '#f44336', text: '#c62828' };
      case 'directional':
        return { bg: '#f3e5f5', border: '#9c27b0', text: '#6a1b9a' };
      case 'space':
        return { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' };
      case 'combining':
        return { bg: '#fff3e0', border: '#ff9800', text: '#e65100' };
      case 'deprecated':
        return { bg: '#f5f5f5', border: '#9e9e9e', text: '#424242' };
      default:
        return { bg: '#f4d5b5', border: '#d9a574', text: '#8b5a2b' };
    }
  };

  const renderHighlightedText = () => {
    if (!text) {
      return <Box variant="p" color="text-body-secondary">No text to analyze</Box>;
    }

    const lines = text.split('\n');
    let currentPos = 0;

    return (
      <div 
        onMouseUp={handleTextSelection}
        style={{ 
          fontFamily: 'monospace',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          backgroundColor: '#fafafa',
          border: '1px solid #d5dbdb',
          borderRadius: '4px',
          padding: '12px',
          maxHeight: '600px',
          overflow: 'auto'
        }}
      >
        {lines.map((line, lineIndex) => {
          const lineStart = currentPos;
          const lineEnd = currentPos + line.length;
          const lineFeedPos = lineEnd; // Position of the line feed character
          currentPos = lineEnd + 1; // +1 for newline

          // Include line feed character if it exists at the end of this line
          const lineDetectedChars = filteredChars.filter(
            d => d.position >= lineStart && d.position <= lineFeedPos
          );

          const lineSegments: React.ReactElement[] = [];
          let lastPos = 0;

          lineDetectedChars.forEach((detected, index) => {
            const relativePos = detected.position - lineStart;
            
            // Add text before invisible character
            if (relativePos > lastPos) {
              const textSegment = line.substring(lastPos, relativePos);
              const segmentStart = lineStart + lastPos;
              const segmentChars = textSegment.split('').map((char, charIdx) => {
                const charPos = segmentStart + charIdx;
                const isHighlighted = highlightedPosition === charPos;
                
                if (isHighlighted) {
                  return (
                    <span
                      key={`char-${charPos}`}
                      style={{
                        backgroundColor: '#ffeb3b',
                        color: '#000',
                        fontWeight: 'bold',
                      }}
                    >
                      {char}
                    </span>
                  );
                }
                return char;
              });
              
              lineSegments.push(
                <span key={`text-${lineIndex}-${index}`}>
                  {segmentChars}
                </span>
              );
            }

            // Add highlighted invisible character
            const colors = showUnicode ? getCharacterColors(detected.character.category) : 
              { bg: '#f4d5b5', border: '#d9a574', text: '#8b5a2b' };
            
            const isInvisibleHighlighted = highlightedPosition === detected.position;
            
            lineSegments.push(
              <Popover
                key={`char-${lineIndex}-${index}`}
                dismissButton={false}
                position="top"
                size="small"
                triggerType="custom"
                content={
                  <Box>
                    <SpaceBetween size="xs">
                      <Box variant="strong">{detected.character.name}</Box>
                      <Box variant="small">{formatUnicodeCode(detected.character.code)}</Box>
                      <Box variant="small">Category: {detected.character.category}</Box>
                    </SpaceBetween>
                  </Box>
                }
              >
                <span
                  onClick={() => handleCharacterClick(detected.character.code)}
                  style={{
                    backgroundColor: isInvisibleHighlighted ? '#ffeb3b' : colors.bg,
                    border: `${isInvisibleHighlighted ? '3px' : '1px'} solid ${isInvisibleHighlighted ? '#ff9800' : colors.border}`,
                    borderRadius: '4px',
                    padding: '2px 6px',
                    color: isInvisibleHighlighted ? '#000' : colors.text,
                    fontWeight: isInvisibleHighlighted ? 'bold' : '500',
                    cursor: allowCopyCharacter ? 'pointer' : 'help',
                    whiteSpace: 'pre',
                    display: 'inline-block',
                    margin: '0 2px',
                  }}
                >
                  {showUnicode ? formatUnicodeCode(detected.character.code) : detected.character.symbol}
                </span>
              </Popover>
            );

            lastPos = relativePos + detected.length;
          });

          // Add remaining text in line
          if (lastPos < line.length) {
            const remainingText = line.substring(lastPos);
            const segmentStart = lineStart + lastPos;
            const remainingChars = remainingText.split('').map((char, charIdx) => {
              const charPos = segmentStart + charIdx;
              const isHighlighted = highlightedPosition === charPos;
              
              if (isHighlighted) {
                return (
                  <span
                    key={`char-${charPos}`}
                    style={{
                      backgroundColor: '#ffeb3b',
                      color: '#000',
                      fontWeight: 'bold',
                    }}
                  >
                    {char}
                  </span>
                );
              }
              return char;
            });
            
            lineSegments.push(
              <span key={`text-${lineIndex}-end`}>
                {remainingChars}
              </span>
            );
          }

          // Check if there's a line feed character at the end of this line
          const lineFeedChar = lineDetectedChars.find(d => d.position === lineFeedPos);
          if (lineFeedChar && lineIndex < lines.length - 1) {
            // Only show line feed if it's not the last line
            const colors = showUnicode ? getCharacterColors(lineFeedChar.character.category) : 
              { bg: '#f4d5b5', border: '#d9a574', text: '#8b5a2b' };
            
            const isLineFeedHighlighted = highlightedPosition === lineFeedPos;
            
            lineSegments.push(
              <Popover
                key={`linefeed-${lineIndex}`}
                dismissButton={false}
                position="top"
                size="small"
                triggerType="custom"
                content={
                  <Box>
                    <SpaceBetween size="xs">
                      <Box variant="strong">{lineFeedChar.character.name}</Box>
                      <Box variant="small">{formatUnicodeCode(lineFeedChar.character.code)}</Box>
                      <Box variant="small">Category: {lineFeedChar.character.category}</Box>
                    </SpaceBetween>
                  </Box>
                }
              >
                <span
                  onClick={() => handleCharacterClick(lineFeedChar.character.code)}
                  style={{
                    backgroundColor: isLineFeedHighlighted ? '#ffeb3b' : colors.bg,
                    border: `${isLineFeedHighlighted ? '3px' : '1px'} solid ${isLineFeedHighlighted ? '#ff9800' : colors.border}`,
                    padding: '0 4px',
                    margin: '0 2px',
                    borderRadius: '3px',
                    color: colors.text,
                    cursor: allowCopyCharacter ? 'pointer' : 'default',
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  {showUnicode 
                    ? formatUnicodeCode(lineFeedChar.character.code)
                    : lineFeedChar.character.symbol
                  }
                </span>
              </Popover>
            );
          }

          return (
            <div key={lineIndex} style={{ display: 'flex', gap: '12px' }}>
              {showLineNumbers && (
                <span style={{ 
                  color: '#9ba7b6', 
                  userSelect: 'none',
                  minWidth: '40px',
                  textAlign: 'right'
                }}>
                  {lineIndex + 1}
                </span>
              )}
              <div style={{ flex: 1 }}>
                {lineSegments.length > 0 ? lineSegments : line || '\u00A0'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <SpaceBetween size="m">
      <div style={{ position: 'relative' }}>
        {renderHighlightedText()}
        {selectionTooltip && (
          <div
            style={{
              position: 'fixed',
              left: `${selectionTooltip.x}px`,
              top: `${selectionTooltip.y}px`,
              transform: 'translate(-50%, -100%)',
              backgroundColor: '#232f3e',
              color: '#ffffff',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1000,
              pointerEvents: 'none'
            }}
          >
            <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
              Selected: "{selectionTooltip.text}"
            </div>
            <div>
              Position: {selectionTooltip.position}
            </div>
          </div>
        )}
      </div>
      {showActionButtons && detectedChars.length > 0 && (
        <SpaceBetween direction="horizontal" size="xs">
          {onRemoveInvisible && (
            <Button onClick={onRemoveInvisible}>Remove Invisible Characters</Button>
          )}
          {onCopyCleaned && (
            <Button onClick={onCopyCleaned}>Copy Cleaned Text</Button>
          )}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

